import { useState, useEffect, useRef, useLayoutEffect } from 'preact/hooks';
import { debounce } from 'debounce';

const isClient = typeof window !== 'undefined';

/**
 * Abstracts away binding/unbinding an event
 * @param {String} eventName Name of the event to subscribe to
 * @param {Function} handler Callback for when event is triggered
 * @param {Element} passedElement Element to bind event handler to, defaults to global
 */
export const useEventListener = (eventName, handler, passedElement) => {
    const savedHandler = useRef();
    const element = passedElement || (isClient ? window : global);

    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);

    useEffect(() => {
        const isSupported = element && element.addEventListener;
        if (!isSupported) {
            return;
        }

        const eventListener = (event) => savedHandler.current(event);
        element.addEventListener(eventName, eventListener, { passive: true });
        return () => {
            element.removeEventListener(eventName, eventListener, {
                passive: true,
            });
        };
    }, [eventName, element]);
};

/**
 * Returns viewport width and height, updates on resize.
 * @return {Object} screen width and height
 */
export const useWindowSize = () => {
    let timeout;

    const getSize = () => ({
        width: isClient ? window.innerWidth : undefined,
        height: isClient ? window.innerHeight : undefined,
    });

    const [windowSize, setWindowSize] = useState(getSize);

    const handleResize = () => {
        clearTimeout(timeout);
        setTimeout(() => setWindowSize(getSize()), 200);
    };
    useEventListener('resize', handleResize, isClient ? window : global);

    return windowSize;
};

export const useIsInView = (margin = '0px') => {
    const [isIntersecting, setIntersecting] = useState(false);
    const ref = useRef();

    useEffect(() => {
        if (!ref.current || !window.IntersectionObserver) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIntersecting(entry.isIntersecting);
            },
            { rootMargin: margin },
        );

        const element = ref.current.base || ref.current;
        observer.observe(element);
        return () => {
            observer.unobserve(element);
        };
    }, []);

    return [ref, isIntersecting];
};

/**
 * Sensor hook that tracks state of CSS media query
 * https://github.com/streamich/react-use/blob/master/docs/useMedia.md
 * @param {String} query media query
 * @param {Boolean} defaultState state of media query in case of SSR
 * @return {Boolean} true if media query matches conditions
 */
export const useMediaQuery = (query = 'all', defaultState = false) => {
    const [state, setState] = useState(
        isClient ? () => window.matchMedia(query).matches : defaultState,
    );

    useEffect(() => {
        let mounted = true;
        const mql = window.matchMedia(query);
        const onChange = () => {
            mounted && setState(Boolean(mql.matches));
        };

        mql.addListener(onChange);
        setState(mql.matches);

        return () => {
            mounted = false;
            mql.removeListener(onChange);
        };
    }, [query]);

    return state;
};

export const useResizeListener = (callback, delay = 10) => {
    useLayoutEffect(() => {
        const onResize = debounce(callback, delay);

        window.addEventListener('resize', onResize, true);

        return () => {
            window.removeEventListener('resize', onResize, true);
        };
    }, [callback, delay]);
};

export const useDidMount = () => {
    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    return isMounted.current;
};
